import * as React from 'react';
import type { BreadcrumbsViewModel } from '~/Features/Breadcrumbs/BreadcrumbsViewModel.csharp';
import { PillTag } from '~/Features/Partials/PillTag/PillTag';
import { BackgroundMediaSection } from '~/Partials/BackgroundMediaSection/BackgroundMediaSection';
import { Breadcrumbs } from '~/Partials/Breadcrumbs/Breadcrumbs';
import type { LinkViewModel } from '~/Partials/Link/LinkViewModel.csharp';
import type { BaseImage } from '~/Partials/Picture/Picture';
import { Picture } from '~/Partials/Picture/Picture';
import { PictureProfiles } from '~/Partials/Picture/PictureProfiles';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { stripFromHTMLNode } from '~/Partials/Utils/utils';
import styles from './StudyHero.module.css';

interface StudyHeroPillTag {
    tagValue: string;
    link?: Pick<LinkViewModel, 'label' | 'href'>;
}

interface StudyHeroProps {
    title: string;
    summary: string;
    info: {
        duration: string;
        level: StudyHeroPillTag;
        locations: StudyHeroPillTag[];
    };
    translations: {
        studyDuration: string;
    };
    breadcrumbs: BreadcrumbsViewModel;
    image: BaseImage;
}

export const StudyHero: React.FC<React.PropsWithChildren<StudyHeroProps>> = props => {
    const { title, summary, image, info, breadcrumbs, translations, children } = props;

    const renderLeftContent = () => (
        <div className={styles.LeftContent}>
            <div className={styles.TagsWrapper}>
                {info.level && (
                    <PillTag type="solid" variant="red" fontColor="light" href={info.level.link?.href}>
                        <SvgIcon iconName="student" />
                        <span className={styles.TagLabel}>{info.level.tagValue}</span>
                    </PillTag>
                )}
                {info.duration && (
                    <PillTag type="solid">
                        <span>
                            <span className={styles.TagDuration}>{translations.studyDuration}:</span>
                            <span className={styles.TagLabel}>{info.duration}</span>
                        </span>
                    </PillTag>
                )}
            </div>
            <h1 dangerouslySetInnerHTML={{ __html: stripFromHTMLNode(title, 'p') }} className={styles.Title} />
            <span className={styles.Summary}>{summary}</span>
        </div>
    );

    const renderRightContent = () => (
        <div className={styles.RightContent}>
            {info.locations && info.locations.length > 0 && (
                <div className={styles.TagsWrapper}>
                    {info.locations.map(location => (
                        <PillTag type="solid" href={location.link?.href} key={location.tagValue}>
                            <SvgIcon iconName="map-pin" />
                            <span className={styles.TagLabel}>{location.tagValue}</span>
                        </PillTag>
                    ))}
                </div>
            )}
            <div className={styles.ChildrenWrapper}>{children}</div>
        </div>
    );

    return (
        <BackgroundMediaSection
            className={styles.StudyHero}
            wrapperClassName={styles.MediaSectionWrapper}
            contentClassName={styles.MediaSectionContent}
            mediaClassName={styles.MediaSectionMedia}
            image={image}
            overlayVariant="leftToRightDarkGreyGradient"
            id="study-page-hero"
        >
            <div className={styles.Content}>
                <Breadcrumbs {...breadcrumbs} className={styles.Breadcrumbs} />
                <div className={styles.ImageWrapper}>
                    <Picture {...image} profile={PictureProfiles.fullWidth} />
                </div>
                <div className={styles.Inner}>
                    {renderLeftContent()}
                    {renderRightContent()}
                </div>
            </div>
        </BackgroundMediaSection>
    );
};
